import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, Bot, Trash2, Loader2, FileDown, ShieldCheck, Languages } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { pdfUrl } from "../../lib/helpers";
import { ReferenceUpload } from "./ReferenceUpload";
import { VoiceInput } from "./VoiceInput";

function ResultBtn({ r, onNav }) {
  const { t } = useLang();
  if (r.type === "pdf") return <a href={pdfUrl(r.href)} target="_blank" rel="noreferrer" className="btn-primary btn-sm mt-3 w-full" data-testid="assistant-result-pdf"><FileDown className="w-4 h-4" /> {t("Open", "Abrir")} {r.label}</a>;
  const cls = r.type === "confirm_send" || r.type === "billing" ? "btn-dark" : "btn-primary";
  const txt = r.type === "confirm_send" ? t("Review and confirm sending", "Revisar y confirmar envío") : r.type === "billing" ? t("Review preview", "Revisar vista previa") : t("Open", "Abrir");
  return <Link to={r.link} onClick={onNav} className={`${cls} btn-sm mt-3 w-full`} data-testid={`assistant-result-${r.type === "confirm_send" ? "confirm-send" : r.type === "billing" ? "billing" : "link"}`}>{r.type !== "pdf" && r.type !== "estimate" && r.type !== "contract" && <ShieldCheck className="w-4 h-4" />} {txt} · {r.label}</Link>;
}

export function AssistantChat({ ctx = {}, compact, onNav, examples = [] }) {
  const { t } = useLang();
  const key = ctx.project_id ? `p:${ctx.project_id}` : ctx.customer_id ? `c:${ctx.customer_id}` : "";
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [refs, setRefs] = useState([]);
  const end = useRef();
  useEffect(() => { api.get("/ai/history", { params: { ctx: key } }).then((r) => setMsgs(r.data)).catch(() => {}); }, [key]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  const ask = async (q) => {
    const m = (q ?? text).trim();
    if (!m || busy) return;
    setText("");
    setMsgs((x) => [...x, { id: Date.now(), role: "user", text: m }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/assistant", { message: m, reference_ids: refs.map((r) => r.id), customer_id: ctx.customer_id || "", project_id: ctx.project_id || "" });
      setRefs([]);
      setMsgs((x) => [...x, { id: Date.now() + 1, role: "assistant", text: data.reply, result: data.result }]);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const clear = async () => { await api.delete("/ai/history", { params: { ctx: key } }); setMsgs([]); };
  const quick = [[t("Translate to English", "Traducir a inglés"), "Translate the last document or message to English, keeping prices, names, addresses and terms exactly."],
    [t("Translate to Spanish", "Traducir a español"), "Traduce el último documento o mensaje al español, manteniendo precios, nombres, direcciones y términos exactos."]];
  return (
    <div className={`flex flex-col ${compact ? "h-full" : "h-[calc(100vh-180px)] lg:h-[calc(100vh-120px)]"}`} data-testid="assistant-chat">
      <div className="flex-1 overflow-y-auto card p-4 grid gap-3 content-start" data-testid="assistant-messages">
        {!msgs.length && <div className="grid gap-2">
          <div className="flex items-center gap-2 text-slate-500"><Bot className="w-5 h-5 text-[#D9531E]" /> {t("Try one of these:", "Pruebe uno de estos:")}</div>
          {examples.map((e, i) => <button key={i} onClick={() => (e.endsWith(": ") ? setText(e) : ask(e))} className="text-left btn-outline !justify-start !font-medium !h-auto py-2" data-testid={`assistant-example-${i}`}>{e}</button>)}
        </div>}
        {msgs.map((m) => (
          <div key={m.id} className={`max-w-[88%] rounded-2xl px-4 py-3 whitespace-pre-wrap text-[15px] ${m.role === "user" ? "ml-auto bg-slate-900 text-white" : "bg-orange-50 border border-orange-100"}`} data-testid={`assistant-msg-${m.role}`}>
            {m.text}{m.result && <ResultBtn r={m.result} onNav={onNav} />}
          </div>
        ))}
        {busy && <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> {t("Thinking…", "Pensando…")}</div>}
        <div ref={end} />
      </div>
      <div className="mt-2 flex flex-wrap gap-2 items-center">
        {quick.map(([l, q], i) => <button key={i} className="btn-ghost btn-sm !px-2" disabled={busy} onClick={() => ask(q)} data-testid={`assistant-quick-${i}`}><Languages className="w-4 h-4" /> {l}</button>)}
        {msgs.length > 0 && <button className="btn-ghost btn-sm !px-2 ml-auto" onClick={clear} data-testid="assistant-clear-btn"><Trash2 className="w-4 h-4" /> {t("Clear", "Borrar")}</button>}
      </div>
      <div className="mt-1"><ReferenceUpload refs={refs} setRefs={setRefs} testid="assistant-ref" /></div>
      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 mt-2">
        <textarea className="inp !min-h-[56px] h-14 flex-1" rows={1} placeholder={t("Type an instruction…", "Escriba una instrucción…")} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }} data-testid="assistant-input" />
        <button className="btn-primary w-14 h-14" disabled={busy} data-testid="assistant-send-btn"><Send className="w-5 h-5" /></button>
        <VoiceInput onText={(s) => setText((x) => (x ? `${x} ${s}` : s).trim())} testid="assistant-voice-btn" />
      </form>
    </div>
  );
}
